// react
import { useMemo } from 'react';

// templates
import { PageTemplate } from '@/pwa/templates/PageTemplate/PageTemplate';

import { PageHeader } from '@/pwa/atoms/PageHeader/PageHeader';
import { SquiggleCard } from '@/pwa/atoms/SquiggleCard/SquiggleCard';
import {
  SuggestedArticles,
  SuggestedArticlesProps,
} from '@/pwa/organisms/SuggestedArticles/SuggestedArticles';

// mui
import { Box, Button, Stack, Typography } from '@mui/material';

// models
import { Account } from '@karehero/models';

export interface DashboardRoutes {
  financialStatus: string;
  careAssessment: string;
  powerOfAttorney: string;
  eligibilityCalculator: string;
  organiser: string;
  careArticle: string;
  wellbeingArticle: string;
  careOptionsArticle: string;
}

export interface DashboardProps {
  suggestedArticles?: SuggestedArticlesProps;
  account: Account;
  description?: string;
  primaryCta: 'chat' | 'book-a-call' | 'both';
  onBookCall: () => void;
  onChat: () => void;
  onToLibrary: () => void;
  onCardClick: (path: string, meta: Record<string, unknown>) => void;

  hasCareAssessment?: boolean;
  hasPoa?: boolean;
  hasBenefitsCalc?: boolean;
  showOrganiser?: boolean;

  routes: DashboardRoutes;

  financialStatusProgress?: number;
  careAssessmentProgress?: number;
  powerOfAttorneyProgress?: number;
}

interface CardDef {
  title: string;
  subtitle: string;
  path: string;
  squiggleColor: 'cherry' | 'lime' | 'warmOrange';
  progress?: number;
}

function buildFirstCards(
  routes: DashboardRoutes,
  hasCareAssessment: boolean,
  financialStatusProgress?: number,
  careAssessmentProgress?: number,
): CardDef[] {
  const financialStatus: CardDef = {
    title: 'Get financial support',
    subtitle:
      'Understand your financial status better with our financial assessment.',
    path: routes.financialStatus,
    squiggleColor: 'lime',
    progress: financialStatusProgress,
  };

  const careCard: CardDef = hasCareAssessment
    ? {
        title: 'Get a care assessment',
        subtitle: 'To get the support you need.',
        path: routes.careAssessment,
        squiggleColor: 'cherry',
        progress: careAssessmentProgress,
      }
    : {
        title: 'Learn more about care',
        subtitle: 'Read about our hidden workforce.',
        path: routes.careArticle,
        squiggleColor: 'cherry',
      };

  return [financialStatus, careCard];
}

function buildAdditionalCards(
  routes: DashboardRoutes,
  hasPoa: boolean,
  hasBenefitsCalc: boolean,
  showOrganiser: boolean,
  powerOfAttorneyProgress?: number,
): CardDef[] {
  const poaCard: CardDef = hasPoa
    ? {
        title: 'Power of Attorney',
        subtitle: 'Get covered',
        path: routes.powerOfAttorney,
        squiggleColor: 'warmOrange',
        progress: powerOfAttorneyProgress,
      }
    : {
        title: 'Your wellbeing',
        subtitle: 'Read about carer self-care and wellbeing.',
        path: routes.wellbeingArticle,
        squiggleColor: 'warmOrange',
      };

  const benefitsCard: CardDef = hasBenefitsCalc
    ? {
        title: 'Could you be missing out?',
        subtitle:
          'Carers miss out on up to £6,000 a year in unclaimed benefits.',
        path: routes.eligibilityCalculator,
        squiggleColor: 'lime',
      }
    : {
        title: 'Explore care options',
        subtitle: 'If someone you know might need extra support soon.',
        path: routes.careOptionsArticle,
        squiggleColor: 'lime',
      };

  const cards: CardDef[] = [poaCard, benefitsCard];

  if (showOrganiser) {
    const organiserCard: CardDef = {
      title: 'Manage your actions',
      subtitle: 'Keep on top of your care experience.',
      path: routes.organiser,
      squiggleColor: 'cherry',
    };
    cards.push(organiserCard);
  }

  return cards;
}

export const Dashboard = ({
  suggestedArticles,
  account,
  description,
  primaryCta,
  onBookCall,
  onChat,
  onToLibrary,
  onCardClick: onNavigate,
  hasCareAssessment = false,
  hasPoa = false,
  hasBenefitsCalc = false,
  showOrganiser = false,
  routes,
  financialStatusProgress,
  careAssessmentProgress,
  powerOfAttorneyProgress,
}: DashboardProps) => {
  const firstCards = useMemo(
    () =>
      buildFirstCards(
        routes,
        hasCareAssessment,
        financialStatusProgress,
        careAssessmentProgress,
      ),
    [
      routes,
      hasCareAssessment,
      financialStatusProgress,
      careAssessmentProgress,
    ],
  );

  const additionalCards = useMemo(
    () =>
      buildAdditionalCards(
        routes,
        hasPoa,
        hasBenefitsCalc,
        showOrganiser,
        powerOfAttorneyProgress,
      ),
    [routes, hasPoa, hasBenefitsCalc, showOrganiser, powerOfAttorneyProgress],
  );

  return (
    <PageTemplate
      maxWidth='md'
      headerSlot={
        <PageHeader
          title={`Welcome, ${account?.firstName}`}
          description={description}
          variant='h1'
        />
      }
      contentSlot={
        <Stack spacing={2}>
          {(primaryCta === 'book-a-call' || primaryCta === 'both') && (
            <Button
              aria-label='book a call'
              size='large'
              variant='contained'
              fullWidth
              onClick={onBookCall}
            >
              Book a call
            </Button>
          )}
          {(primaryCta === 'chat' || primaryCta === 'both') && (
            <Button
              aria-label='chat'
              size='large'
              variant={primaryCta === 'both' ? 'outlined' : 'contained'}
              fullWidth
              onClick={onChat}
            >
              Chat with us
            </Button>
          )}
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2,
              alignItems: 'stretch',
            }}
          >
            {firstCards.map((card, i) => (
              <SquiggleCard
                key={i}
                squiggleColor={card.squiggleColor}
                squiggleFlipped
                title={card.title}
                subtitle={card.subtitle}
                progress={card.progress}
                chevron
                onClick={() => onNavigate(card.path, { cardTitle: card.title })}
                sx={{ flex: '1 1 160px', minWidth: 160 }}
              />
            ))}
          </Box>
          {additionalCards.map((card, i) => (
            <SquiggleCard
              key={i}
              squiggleColor={card.squiggleColor}
              squiggleFlipped
              title={card.title}
              subtitle={card.subtitle}
              progress={card.progress}
              chevron
              onClick={() => onNavigate(card.path, { cardTitle: card.title })}
            />
          ))}
          <Stack
            sx={{
              gap: 1,
            }}
          >
            <Stack
              direction='row'
              sx={{
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography variant='h2'>Suggested articles</Typography>
              <Button aria-label='See all' onClick={onToLibrary}>
                See all
              </Button>
            </Stack>
            <SuggestedArticles {...suggestedArticles} />
          </Stack>
        </Stack>
      }
    />
  );
};

Dashboard.displayName = 'Dashboard';

export default Dashboard;
